import { useMemo } from 'react';

import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';

type BuildingOption = { id: string; name: string };

// The facility set the inbound-referral surfaces (create + both edit screens) offer, scoped to
// Skilled Nursing (OLIO-3344). `facilities` is the raw SNF-scoped list (used where a real SNF is
// wanted, e.g. the provider-NPI prefill); `facilityOptions` is what the pickers show:
//
//   the SNF set ∪ the buildings already attached to this referral, deduped by id, sorted by name.
//
// The union keeps an already-attached building selectable even when it falls outside the SNF scope
// (attached before this scope existed), so editing can't silently drop it and revoke its access
// (AC4). Sorting the merged list means an out-of-scope building lands by name rather than tacked
// on after the API's sorted set. Shared by ReferralModal and ReferralForm so the two edit surfaces
// can't drift on what's attachable to one referral.
export function useReferralFacilities(selectedBuildings: BuildingOption[]) {
  const { facilities, isPending } = useAllowedFacilities({ type: SKILLED_NURSING_FACILITY });

  const facilityOptions = useMemo<BuildingOption[]>(() => {
    const byId = new Map<string, BuildingOption>();
    facilities.forEach((facility) => byId.set(facility.id, { id: facility.id, name: facility.name }));
    selectedBuildings.forEach((building) => {
      if (!byId.has(building.id)) byId.set(building.id, building);
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities, selectedBuildings]);

  return { facilities, facilityOptions, isPending };
}
